import { useEffect, useState } from 'react';

/**
 * Tracks which section id is currently in view, for the nav highlight —
 * "in view" meaning nearest the vertical band a third of the way down the
 * viewport, matching where a reader's eye actually sits while scrolling.
 *
 * Deliberately not `IntersectionObserver`-based. `useInView` (the section
 * reveal hook) hit a real bug this same phase: intersection is computed from
 * actually-*rendered* frames, so an instant same-page jump (nav click,
 * Home/End key, scroll restoration) can skip straight from one position to
 * another without ever rendering a frame where an in-between section
 * intersected — leaving stale state behind. A plain `scroll`/`resize`
 * listener that re-reads `getBoundingClientRect()` doesn't have that gap:
 * every jump, instant or not, still fires a real `scroll` event at the
 * destination, and the check re-derives the answer straight from current
 * geometry rather than from a frame-dependent transition.
 *
 * The target sections are `React.lazy` chunks inside one shared `Suspense`
 * boundary in `App.tsx`, while `Nav` itself is not — so on first mount none
 * of `ids` exist in the DOM yet. A `MutationObserver` retries once the lazy
 * sections actually mount, then disconnects; it only exists in that startup
 * window, not for the component's whole lifetime.
 */
export function useActiveSection(ids: readonly string[]): string {
  const [active, setActive] = useState(ids[0] ?? '');

  useEffect(() => {
    let mutationObserver: MutationObserver | null = null;
    let cleanupListeners: (() => void) | null = null;
    let attachedCount = 0;

    const attach = (): boolean => {
      const sections = ids
        .map((id) => document.getElementById(id))
        .filter((el): el is HTMLElement => el !== null);

      // Nothing new appeared since the last attach — leave the working
      // listeners alone rather than tearing them down and rebuilding them on
      // every unrelated DOM mutation.
      if (sections.length <= attachedCount) return sections.length === ids.length;
      attachedCount = sections.length;
      cleanupListeners?.();

      // Attach as soon as *any* tracked section exists, not once they all do.
      // Requiring the full set means one missing id silently disables the
      // whole hook — which is exactly what happened while sections were still
      // being built out, leaving the readout frozen on the first entry. The
      // MutationObserver below keeps re-attaching as more mount, so a partial
      // attach self-corrects instead of being a dead end.
      if (sections.length === 0) return false;

      const check = () => {
        // "The last section whose top edge the reader has passed", not
        // "the section whose centre is nearest".
        //
        // Centre-distance is the obvious approach and it breaks badly the
        // moment sections differ in height. Selected Work is ~4700px tall (it
        // is a scroll-driven track), so while the reader is right inside it,
        // its centre is still thousands of pixels away and the short hero
        // above wins — the readout confidently announced "01 INTRO" through
        // the entire Work section. Top-edge order has no such failure mode
        // and degrades sensibly for any section height.
        const bandY = window.innerHeight * 0.45;
        let current = sections[0]!.id;
        for (const el of sections) {
          if (el.getBoundingClientRect().top <= bandY) current = el.id;
        }
        setActive(current);
      };

      check();
      window.addEventListener('scroll', check, { passive: true });
      window.addEventListener('resize', check, { passive: true });
      cleanupListeners = () => {
        window.removeEventListener('scroll', check);
        window.removeEventListener('resize', check);
      };
      // Only "done" once every tracked section is present; a partial attach
      // works but must keep watching for the rest.
      return sections.length === ids.length;
    };

    if (!attach()) {
      mutationObserver = new MutationObserver(() => {
        if (attach()) {
          mutationObserver?.disconnect();
          mutationObserver = null;
        }
      });
      mutationObserver.observe(document.body, { childList: true, subtree: true });
    }

    return () => {
      mutationObserver?.disconnect();
      cleanupListeners?.();
    };
  }, [ids]);

  return active;
}
